import { useFormik } from "formik";
import React, { useCallback, useState } from "react";
import { useSnackbar } from "notistack";
import bytes from "bytes";

import * as yup from "yup";

import AccordionDetails from "@material-ui/core/AccordionDetails";
import { Accordion, AccordionSummary } from "../shared/Accordion";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Paper from "@material-ui/core/Paper";

import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import ListItemText from "@material-ui/core/ListItemText";
import Alert from "@material-ui/lab/Alert";

import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import AddIcon from "@material-ui/icons/Add";

import { uploadFile, validateIframeUrl, uploadFiles } from "../../api";
import Timestamps from "../Timestamps";
import ImageUpload from "../ImageUpload";
import { getFieldProps, inputProps } from "../utils";
import InfoTooltip from "../InfoTooltip";
import FileUpload from "../shared/FileUpload";

import SemanticsSelect from "../shared/SemanticsSelect";
import SurveySelect from "../shared/SurveySelect";
import { useAppContext } from "../../api/AppContext";
import { isCdpOnlyUser } from "../../lib/companyAccess";

const SelectSemanticMaterial = ({
  alreadySelectedFlowIds,
  formik,
  journeyId,
  locale,
}) => {
  // this will add to formik.values three mandatory fields: flowId, flowCompleteSemanticIds, flowCompleteType (cta-clicked or tip-reached)
  // flowId should not be already selected in other materials (preserve 1-1 relationship materials-flows)
  return (
    <Grid container spacing={2}>
      <Grid item md={4} xs={12}>
        <SemanticsSelect
          required
          filter={(semantic) =>
            semantic.isFirstInFlow &&
            (semantic.flowId === formik.values.flowId ||
              !alreadySelectedFlowIds.includes(semantic.flowId))
          }
          journeyId={journeyId}
          label="Message"
          locale={locale}
          primaryColumn="flowId"
          {...getFieldProps(formik, {
            name: "flowId",
          })}
          onChange={(value) => {
            formik.setFieldValue("flowId", value.flowId);
            formik.setFieldTouched("flowId", true);
            formik.setFieldValue("flowCompleteSemanticIds", []);
            formik.setFieldTouched("flowCompleteSemanticIds", true);
          }}
        />
      </Grid>
      <Grid item md={4} xs={12}>
        <TextField
          fullWidth
          select
          InputLabelProps={{ shrink: true }}
          margin="none"
          variant="outlined"
          {...getFieldProps(formik, {
            name: "flowCompleteType",
          })}
          label="The material can be considered completed when *"
        >
          <MenuItem value="cta-clicked">{`CTA clicked`}</MenuItem>
          <MenuItem value="tip-reached">Tip reached</MenuItem>
        </TextField>
      </Grid>
      <Grid item md={4} xs={12}>
        <SemanticsSelect
          fullWidth
          multiple
          required
          disableClearable={false}
          filter={(item) => item.flowId === formik.values.flowId}
          journeyId={journeyId}
          label="Select which tip(s) will complete the material"
          locale={locale}
          {...getFieldProps(formik, {
            disabled: !formik.values.flowId,
            helperText:
              !formik.values.flowId &&
              'Select message first in "Message" field',
            name: "flowCompleteSemanticIds",
          })}
          onChange={(values) => {
            formik.setFieldValue(
              "flowCompleteSemanticIds",
              values.map(({ id }) => id)
            );
            formik.setFieldTouched("flowCompleteSemanticIds", true);
          }}
        />
      </Grid>
    </Grid>
  );
};

const editSchema = yup.object().shape({
  ctaUrl: yup
    .string()
    .url()
    .when("type", {
      is: (type) => type === "EXTERNAL" || type === "WEBPAGE",
      then: yup
        .string()
        .url()
        .required()
        .when("shouldOpenInBrowser", {
          is: (shouldOpenInBrowser) => !shouldOpenInBrowser,
          then: yup
            .string()
            .url()
            .required()
            .test(
              "ctaUrl",
              'Your selected url does not allow other sites to embed it. Please change URL or select "Open in browser".',
              async (value) => {
                try {
                  if (!yup.string().url().isValidSync(value)) {
                    return true;
                  }

                  const { valid } = await validateIframeUrl({
                    url: value,
                  });

                  return valid;
                } catch (e) {
                  return false;
                }
              }
            ),
          otherwise: yup.string().url().required(),
        }),
      otherwise: yup.string().when("type", {
        is: (type) => type === "SEMANTIC",
        then: yup.string().nullable(),
        otherwise: yup.string().when("type", {
          is: (type) => type === "SURVEY",
          then: yup.string().when("isContentPortalSurvey", {
            is: (v) => v === true,
            then: yup.string().nullable(),
            otherwise: yup
              .string()
              .url()
              .when("shouldOpenInBrowser", {
                is: (shouldOpenInBrowser) => !shouldOpenInBrowser,
                then: yup
                  .string()
                  .url()
                  .required()
                  .test(
                    "ctaUrl",
                    'Your selected url does not allow other sites to embed it. Please change URL or select "Open in browser".',
                    async (value) => {
                      try {
                        if (!yup.string().url().isValidSync(value)) {
                          return true;
                        }

                        const { valid } = await validateIframeUrl({
                          url: value,
                        });

                        return valid;
                      } catch (e) {
                        return false;
                      }
                    }
                  ),
                otherwise: yup.string().url().required(),
              }),
          }),
          otherwise: yup.string().url().required(),
        }),
      }),
    }),
  coverImageDimensions: yup
    .string()
    .oneOf(["1x1", "2x1", "3x1"], "Invalid cover image dimensions")
    .nullable(),
  flowId: yup
    .string()
    .nullable()
    .when("type", {
      is: (type) => type === "SEMANTIC",
      otherwise: yup.string().nullable(),
      then: yup.string().required("Message flow id is required"),
    }),
  flowCompleteSemanticIds: yup.array().when("type", {
    is: (type) => type === "SEMANTIC",
    then: yup
      .array()
      .of(yup.string().required())
      .min(1, "Select at least a tip to complete the material"),
    otherwise: yup.array().of(yup.string()).nullable(),
  }),
  flowCompleteType: yup.string().when("type", {
    is: (type) => type === "SEMANTIC",
    then: yup.string().oneOf(["cta-clicked", "tip-reached"]).required(),
    otherwise: yup.string().nullable(),
  }),
  image: yup.string(),
  small_image: yup.string().nullable(),
  name: yup.string().required(),
  description: yup.string().nullable(),
  shouldOpenInBrowser: yup.boolean().required(),
  allowTracking: yup.boolean().when("type", {
    is: "VIDEO_UPLOAD",
    then: yup.boolean().required(),
    otherwise: yup.boolean().nullable(),
  }),
  disableSkip: yup.boolean().when("type", {
    is: "VIDEO_UPLOAD",
    then: yup.boolean().required(),
    otherwise: yup.boolean().nullable(),
  }),
  video_duration: yup.number().nullable().when("type", {
    is: "VIDEO_UPLOAD",
    then: yup.number().nullable(),
    otherwise: yup.number().nullable(),
  }),
  surveyId: yup
    .string()
    .nullable()
    .when("type", {
      is: (type) => type === "SURVEY",
      then: yup
        .string()
        .nullable()
        .when("isContentPortalSurvey", {
          is: (v) => v === true,
          then: yup.string().required("Survey is required"),
          otherwise: yup.string().nullable(),
        }),
      otherwise: yup.string().nullable(),
    }),
  type: yup
    .string()
    .oneOf([
      "VIDEO",
      "IMAGE",
      "DOCUMENT",
      "WEBPAGE",
      "EXTERNAL",
      "SEMANTIC",
      "SURVEY",
      "VIDEO_UPLOAD",
      "GAME",
    ])
    .required(),
  stepId: yup.string().required(),
  isContentPortalSurvey: yup.boolean().nullable(),
  showLMInHome: yup.boolean().required(),
});

const useEditMaterial = ({
  alreadySelectedFlowIds,
  material,
  locale,
  onUpdate,
  journey,
  journeys,
  isDisabled = false,
}) => {
  const {
    name,
    type,
    ctaUrl,
    shouldOpenInBrowser,
    flowId,
    flowCompleteSemanticIds,
    flowCompleteType,
    surveyId,
  } = material;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...material,
      ctaUrl: ctaUrl || "",
      name: name || "",
      shouldOpenInBrowser: shouldOpenInBrowser || false,
      type: type || "WEBPAGE",
      flowId: flowId || undefined,
      flowCompleteType: flowCompleteType || "tip-reached",
      flowCompleteSemanticIds: flowCompleteSemanticIds || [],
      surveyId: surveyId || undefined,
      allowTracking: material.allowTracking || false,
      coverImageDimensions: material.coverImageDimensions || undefined,
      disableSkip: material.disableSkip || false,
      video_duration: material.video_duration || null,
      isContentPortalSurvey: material.isContentPortalSurvey || true,
      showLMInHome: material.showLMInHome || false,
    },
    onSubmit: (values) => onUpdate(values),
    validationSchema: editSchema,
    validator: () => ({}),
  });

  const [isFileUploading, setIsFileUploading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAppContext();
  const isCdpUser = isCdpOnlyUser(user);

  const handleUpload = useCallback(
    ({ url }) => {
      formik.setFieldValue("image", url);
      formik.setFieldTouched("image");
    },
    [formik]
  );

  const handleImageUpload = useCallback(
    ({ url }) => {
      formik.setFieldValue("ctaUrl", url);
      formik.setFieldTouched("ctaUrl");
    },
    [formik]
  );
  const handleSmallImageUpload = useCallback(
    ({ url }) => {
      formik.setFieldValue("small_image", url);
      formik.setFieldTouched("small_image");
    },
    [formik]
  );
  const handleFileUpload = useCallback(
    async (args) => {
      setIsFileUploading(true);

      try {
        const { url } = await uploadFile({
          ...args,
          customPathInfo: { journeyId: journey.id },
        });

        await formik.setFieldValue("ctaUrl", url);
        await formik.setFieldTouched("ctaUrl");
      } catch (e) {
        enqueueSnackbar(`Upload error: ${e.message}`, {
          variant: "error",
        });
      } finally {
        setIsFileUploading(false);
      }
    },
    [enqueueSnackbar, formik, journey.id]
  );
  /* const handleVideoUpload = useCallback(
    async (args) => {
      setIsFileUploading(true);

      try {
        const { url } = await uploadFile({
          ...args,
          customPathInfo: { journeyId: journey.id },
        });

        setIsFileUploading(false);

        const video = document.createElement("video");
        video.src = url;

        const videoDuration = await new Promise((resolve) => {
          video.onloadedmetadata = () => {
            resolve(Math.round(video.duration));
          };

          video.onerror = (e) => {
            console.error("Error loading video:", e);
            resolve(null);
          };

          setTimeout(() => {
            resolve(null);
          }, 10000);
        });

        console.log("Extracted video duration:", videoDuration);

        await formik.setFieldValue("ctaUrl", url);
        await formik.setFieldTouched("ctaUrl");

        if (videoDuration !== null) {
          await formik.setFieldValue("video_duration", videoDuration);
          await formik.setFieldTouched("video_duration");
        }
      } catch (e) {
        console.error("Video upload error:", e);
        enqueueSnackbar(`Upload video error: ${e.message}`, {
          variant: "error",
        });
        setIsFileUploading(false);
      }
    },
    [formik, journey.id, enqueueSnackbar]
  ); */

  const handleHTMZipUpload = useCallback(
    async (args) => {
      setIsFileUploading(true);

      try {
        const { url } = await uploadFiles(args);
        await formik.setFieldValue("ctaUrl", url);
        await formik.setFieldTouched("ctaUrl");
      } catch (e) {
        enqueueSnackbar(`Upload Genially zip error: ${e.message}`, {
          variant: "error",
        });
      } finally {
        setIsFileUploading(false);
      }
    },
    [enqueueSnackbar, formik]
  );

  const handleTypeChange = useCallback(
    (e) => {
      formik.setFieldValue("type", e.target.value);
      formik.setFieldTouched("type");

      if (e.target.value === "EXTERNAL") {
        return;
      }

      formik.setFieldValue("shouldOpenInBrowser", false);
      formik.setFieldTouched("shouldOpenInBrowser");

      formik.setTouched({}, false); //reset validation
    },
    [formik]
  );

  const surveySelectorFilterFunction = (survey) => {
    return (
      !!survey?.distributionChannels?.link?.isEnabled && survey.type !== "hi"
    );
  };

  return {
    formik,
    ui: (
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Timestamps
            copiedFromEntity="learning-material"
            copiedFromEntityId={material.copiedFromMaterialId}
            copiedFromJourneyId={material.copiedFromJourneyId}
            createdAt={material.createdAt}
            createdBy={material.createdBy}
            journeys={journeys}
            locale={locale}
            updatedAt={material.updatedAt}
            updatedBy={material.updatedBy}
          />
        </Grid>
        {!material.stepId && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              InputLabelProps={{ shrink: true }}
              label="Step"
              variant="outlined"
              {...getFieldProps(formik, {
                disabled: isDisabled,
                name: "stepId",
              })}
            >
              {(journey?.steps ?? []).map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            {...(material.step && { autoFocus: true })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={inputProps}
            label="Name"
            margin="none"
            placeholder="Material name..."
            variant="outlined"
            {...getFieldProps(formik, { disabled: isDisabled, name: "name" })}
          />
          {
            // Note: once all clients are on leaf > v.112, this alert won't be needed anymore
            getFieldProps(formik, { name: "name" }).value &&
              /[^A-Za-z0-9 ]+/g.test(
                getFieldProps(formik, { name: "name" }).value
              ) && (
                <Alert severity="warning">
                  Special characters different from standard alphabet, numbers
                  or spaces may not work when a tip CTA is linked to them.
                  Either remove them or simulate the tip and its CTA in your hi
                  client (not from content portal).
                </Alert>
              )
          }
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item>
                  <Typography style={{ fontWeight: "bolder" }}>
                    Cover Image
                  </Typography>
                </Grid>
                <Grid item>
                  <InfoTooltip
                    text="The picture uploaded will be resized to be with width 680px and height 383px. The picture will be automatically cropped"
                    title="Image Dimensions"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <ImageUpload
                buttonProps={{ children: "Upload", fullWidth: false }}
                caption={
                  <Typography
                    color="textSecondary"
                    style={{ color: "#BFBFBF", fontStyle: "italic" }}
                    variant="caption"
                  >
                    The picture uploaded will be resized to be with width 680px
                    and height 383px. The picture will be automatically cropped.
                  </Typography>
                }
                customPathInfo={{ journeyId: journey.id }}
                disabled={formik.isSubmitting || isDisabled}
                errorMessage={
                  getFieldProps(formik, { name: "image" }).helperText
                }
                value={formik.values.image}
                onChange={handleUpload}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography style={{ fontWeight: "bolder" }}>
                Cover Image Dimensions
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                InputLabelProps={{ shrink: true }}
                margin="none"
                variant="outlined"
                {...getFieldProps(formik, {
                  name: "coverImageDimensions",
                })}
                label="Aspect ratio"
              >
                <MenuItem value="1x1">Squared 1x1</MenuItem>
                <MenuItem value="2x1">Rect 2x1</MenuItem>
                <MenuItem value="3x1">Rect 3x1</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item>
                  <Typography style={{ fontWeight: "bolder" }}>
                    Small Image
                  </Typography>
                </Grid>
                <Grid item>
                  <InfoTooltip
                    text="The picture uploaded will be resized to be with width 227px and height 150px. The picture will be automatically cropped"
                    title="Image Dimensions"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <ImageUpload
                buttonProps={{ children: "Upload", fullWidth: false }}
                caption={
                  <Typography
                    color="textSecondary"
                    style={{ color: "#BFBFBF", fontStyle: "italic" }}
                    variant="caption"
                  >
                    The picture uploaded will be resized to be with width 227px
                    and height 150px. The picture will be automatically cropped.
                  </Typography>
                }
                customPathInfo={{ journeyId: journey.id }}
                disabled={formik.isSubmitting}
                errorMessage={
                  getFieldProps(formik, { name: "small_image" }).helperText
                }
                value={formik.values.small_image}
                onChange={handleSmallImageUpload}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container item alignItems="center" spacing={1} xs={12}>
          <Grid item>
            <Typography variant="h6">Learning Material content</Typography>
          </Grid>
          <Grid item>
            <InfoTooltip
              text="This is the Learning Material that will appear in the user Dashboard. You can upload .jpg, .png, and .pdf files. For videos, we suggest uploading them to an external platform (e.g., Vimeo) and add here the link. You can also add the link for any Learning Material that is available as a web page: be sure it has “https://” at the beginning of the URL, otherwise it won’t be uploaded."
              title="How to upload a file correctly?"
            />
          </Grid>
        </Grid>
        <Grid item xs={8}>
          <Grid container spacing={4}>
            {formik.values.type === "SEMANTIC" && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  This configuration is not supported in teams app and clients
                  without websocket or polling.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                InputLabelProps={{ shrink: true }}
                InputProps={inputProps}
                disabled={isDisabled}
                label="File type"
                margin="none"
                name="type"
                value={formik.values.type}
                variant="outlined"
                onChange={handleTypeChange}
              >
                <MenuItem key="WEBPAGE" value="WEBPAGE">
                  Contenuti interattivi
                </MenuItem>
                <MenuItem key="DOCUMENT" value="DOCUMENT">
                  Document
                </MenuItem>
                <MenuItem key="IMAGE" value="IMAGE">
                  Image (.jpg, .png)
                </MenuItem>
                <MenuItem key="EXTERNAL" value="EXTERNAL">
                  External links (New dashboard only)
                </MenuItem>
                <MenuItem key="GAME" value="GAME">
                  Game (New dashboard only)
                </MenuItem>
                {!isCdpUser && (
                  <MenuItem key="SURVEY" value="SURVEY">
                    Survey
                  </MenuItem>
                )}
                <MenuItem key="SEMANTIC" value="SEMANTIC">
                  Tip
                </MenuItem>
                <MenuItem key="VIDEO" value="VIDEO">
                  Video
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              {journey && formik.values.type === "SEMANTIC" && (
                <SelectSemanticMaterial
                  alreadySelectedFlowIds={alreadySelectedFlowIds}
                  formik={formik}
                  journeyId={journey.id}
                  locale={locale}
                ></SelectSemanticMaterial>
              )}
              {journey &&
                formik.values.type === "SURVEY" &&
                formik.values.isContentPortalSurvey && (
                  <SurveySelect
                    filterfunction={surveySelectorFilterFunction}
                    formik={formik}
                    journeyId={journey.id}
                  ></SurveySelect>
                )}
              {(() => {
                // Show for DOCUMENT always (upload populates ctaUrl),
                // show for SURVEY only when isContentPortalSurvey is unchecked,
                // hide for SEMANTIC, show for all others
                const show =
                  (formik.values.type !== "SEMANTIC" &&
                    formik.values.type !== "SURVEY") ||
                  (formik.values.type === "SURVEY" &&
                    !formik.values.isContentPortalSurvey);
                if (!show) return null;
                return (
                  <>
                    <TextField
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={inputProps}
                      label="Learning Material Link"
                      margin="none"
                      placeholder="https://"
                      required={
                        (formik.values.type === "DOCUMENT" &&
                          formik.values.shouldOpenInBrowser) ||
                        (formik.values.type === "SURVEY" &&
                          !formik.values.isContentPortalSurvey)
                      }
                      variant="outlined"
                      {...getFieldProps(formik, {
                        disabled: isDisabled,
                        name: "ctaUrl",
                      })}
                    />
                    {["DOCUMENT", "WEBPAGE"].includes(formik.values.type) &&
                      isFileUploading && (
                        <Box mt={1}>
                          <Alert severity="info">
                            Upload in progress. The file is being scanned for
                            security and may take a few minutes to become
                            available.
                          </Alert>
                        </Box>
                      )}
                  </>
                );
              })()}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.showLMInHome}
                    color="primary"
                    disabled={isDisabled}
                    name="showLMInHome"
                    onChange={formik.handleChange}
                  />
                }
                label="Show this LM in Home"
              />
            </Grid>
          </Grid>
          {[
            "WEBPAGE",
            "EXTERNAL",
            "GAME",
            "VIDEO",
            "DOCUMENT",
            "SURVEY",
          ].includes(formik.values.type) && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.shouldOpenInBrowser}
                      color="primary"
                      name="shouldOpenInBrowser"
                      onChange={formik.handleChange}
                    />
                  }
                  label="Open in browser"
                />
              </Grid>
              {/* Genially files are now uploaded using zip*/}
              {["WEBPAGE", "GAME", "EXTERNAL"].includes(formik.values.type) && (
                <Grid item xs={12}>
                  <FileUpload
                    accept=".zip"
                    onChange={(args) =>
                      handleHTMZipUpload({
                        ...args,
                        customPathInfo: { journeyId: journey.id },
                      })
                    }
                  >
                    {({ upload, error }) => (
                      <>
                        <Button
                          color="primary"
                          disabled={isFileUploading || isDisabled}
                          endIcon={
                            isFileUploading && <CircularProgress size={25} />
                          }
                          startIcon={<AddIcon />}
                          variant="outlined"
                          onClick={upload}
                        >
                          Upload HTML zip
                        </Button>
                        {error && (
                          <div>
                            <Typography color="error" variant="caption">
                              {error}
                            </Typography>
                          </div>
                        )}
                      </>
                    )}
                  </FileUpload>
                </Grid>
              )}
            </Grid>
          )}

          {(formik.values.type === "VIDEO" ||
            formik.values.type === "VIDEO_UPLOAD") && (
            <Grid container spacing={2}>
              {!formik.values.shouldOpenInBrowser && (
                <Grid item xs={12}>
                  <Box alignItems="center" display="flex">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.allowTracking}
                          color="primary"
                          name="allowTracking"
                          onChange={formik.handleChange}
                        />
                      }
                      label="Allow Tracking"
                    />
                    <InfoTooltip
                      text="When enabled, the system will track user's video viewing progress"
                      title="Allow Tracking"
                    />
                  </Box>
                </Grid>
              )}
              {!formik.values.shouldOpenInBrowser && (
                <Grid item xs={12}>
                  <Box alignItems="center" display="flex">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.disableSkip}
                          color="primary"
                          name="disableSkip"
                          onChange={formik.handleChange}
                        />
                      }
                      label="Disable Skip"
                    />
                    <InfoTooltip
                      text="When enabled, users cannot skip ahead in the video"
                      title="Disable Skip"
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          )}

          {["DOCUMENT", "IMAGE", "VIDEO_UPLOAD"].includes(
            formik.values.type
          ) && (
            <Grid container alignContent="flex-start" spacing={2}>
              <Grid item>
                {formik.values.type === "DOCUMENT" && (
                  <FileUpload
                    accept=".pdf"
                    disabled={isDisabled}
                    maxSize={bytes("10 MB")}
                    readAs="DataURL"
                    onChange={handleFileUpload}
                  >
                    {({ upload, error, value }) => (
                      <>
                        <Button
                          color="primary"
                          disabled={isFileUploading}
                          endIcon={
                            isFileUploading && <CircularProgress size={25} />
                          }
                          startIcon={<AddIcon />}
                          variant="outlined"
                          onClick={upload}
                        >
                          Upload
                        </Button>
                        {error && (
                          <div>
                            <Typography color="error" variant="caption">
                              {error}
                            </Typography>
                          </div>
                        )}
                        {value && (
                          <Box mt={2}>
                            <Typography variant="subtitle2">
                              <a
                                href={
                                  isFileUploading
                                    ? value.body
                                    : formik.values.ctaUrl
                                }
                                rel="noreferrer noopener"
                                target="_blank"
                                {...(isFileUploading
                                  ? { download: value.name }
                                  : {})}
                              >
                                <Box alignItems="center" display="flex">
                                  <Box mr={1}>{value.name}</Box>
                                  <OpenInNewIcon fontSize="small" />
                                </Box>
                              </a>
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </FileUpload>
                )}
                {formik.values.type === "IMAGE" && (
                  <ImageUpload
                    customPathInfo={{ journeyId: journey.id }}
                    disabled={isDisabled}
                    value={formik.values.ctaUrl}
                    onChange={handleImageUpload}
                  />
                )}
                {/* {formik.values.type === "VIDEO_UPLOAD" && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FileUpload
                        accept=".mp4,.webm,.mov,.avi"
                        disabled={isDisabled}
                        maxSize={bytes("100 MB")}
                        readAs="DataURL"
                        onChange={handleVideoUpload}
                      >
                        {({ upload, error, value }) => (
                          <>
                            <Button
                              color="primary"
                              disabled={isFileUploading}
                              endIcon={
                                isFileUploading && (
                                  <CircularProgress size={25} />
                                )
                              }
                              startIcon={<AddIcon />}
                              variant="outlined"
                              onClick={upload}
                            >
                              Upload Video
                            </Button>
                            {error && (
                              <div>
                                <Typography color="error" variant="caption">
                                  {error}
                                </Typography>
                              </div>
                            )}
                            {value && (
                              <Box mt={2}>
                                <Typography variant="subtitle2">
                                  <a
                                    href={
                                      isFileUploading
                                        ? value.body
                                        : formik.values.ctaUrl
                                    }
                                    rel="noreferrer noopener"
                                    target="_blank"
                                    {...(isFileUploading
                                      ? { download: value.name }
                                      : {})}
                                  >
                                    <Box alignItems="center" display="flex">
                                      <Box mr={1}>{value.name}</Box>
                                      <OpenInNewIcon fontSize="small" />
                                    </Box>
                                  </a>
                                </Typography>
                              </Box>
                            )}
                          </>
                        )}
                      </FileUpload>
                    </Grid>
                  </Grid>
                )} */}
              </Grid>
            </Grid>
          )}

          {formik.values.type === "SURVEY" && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.isContentPortalSurvey}
                      color="primary"
                      name="isContentPortalSurvey"
                      onChange={formik.handleChange}
                    />
                  }
                  label="Configured in Content Portal"
                />
              </Grid>
            </Grid>
          )}
        </Grid>

        {formik.values.type === "VIDEO" &&
          !formik.values.shouldOpenInBrowser && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6">FAQ</Typography>
              </Grid>
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body1">
                      How do I get the proper URL for a video from Youtube or
                      Vimeo?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Paper>
                      <Box p={2}>
                        <ul>
                          <li>
                            <Typography variant="body1">
                              Either in Youtube or Vimeo, in the page where the
                              video is opened, click on “Share”
                            </Typography>
                          </li>
                          <li>
                            <Typography variant="body1">
                              Click the “Embed” (”Incorpora” in italian) option
                            </Typography>
                          </li>
                          <li>
                            <Typography variant="body1">
                              copy what’s inside quotes of src
                            </Typography>
                            <Box pb={2} pt={2}>
                              <img
                                alt=""
                                src="/materials/faq/embedsrc.png"
                                style={{ maxWidth: "100%" }}
                              />
                            </Box>
                          </li>
                          <li>
                            <Typography variant="body1">
                              If you want to add autoplay
                              <ul>
                                <li>
                                  If the URL copied already contains a ? → add
                                  to that URL &autoplay=1
                                </li>
                                <li>Otherwise → add to that URL ?autoplay=1</li>
                              </ul>
                            </Typography>
                          </li>
                        </ul>
                      </Box>
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </>
          )}
        <Grid item md={8} xs={12}>
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={inputProps}
            label="Description"
            margin="none"
            placeholder="Description..."
            variant="outlined"
            {...getFieldProps(formik, {
              disabled: isDisabled,
              name: "description",
            })}
          />
        </Grid>
      </Grid>
    ),
  };
};

export default useEditMaterial;
